import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BulkResumeUploadService, BulkUploadSummary, UploadProgress } from 'src/app/core/services/bulk-resume-upload.service';
import { WebSocketsService } from 'src/app/core/services/web-sockets.service';
import { BulkUploadResumeActionsComponent } from '../bulk-upload-resume-actions/bulk-upload-resume-actions.component';

// Lives in the app shell so a running upload and its result survive navigation away from the upload page.
@Component({
  selector: 'app-bulk-upload-status',
  templateUrl: './bulk-upload-status.component.html',
  styleUrls: ['./bulk-upload-status.component.css']
})
export class BulkUploadStatusComponent implements OnInit, OnDestroy {

  progress: UploadProgress;
  summary: BulkUploadSummary;

  private modalRef: BsModalRef;
  private uploadActive = false;
  private subscriptions = new Subscription();

  constructor(
    private bulkUploadService: BulkResumeUploadService,
    private bsModalService: BsModalService,
    private webSocketsService: WebSocketsService,
    private zone: NgZone
  ) { }

  ngOnInit(): void {
    // progress$ replays its last value, so a freshly created shell would otherwise mistake an upload
    // the user already minimized for a new one and pop the modal open again.
    const current = this.bulkUploadService.currentProgress;
    this.progress = current;
    this.summary = this.bulkUploadService.completedSummary;
    this.uploadActive = !!current && current.isProcessing;
    this.subscriptions.add(this.bulkUploadService.progress$.subscribe(progress => this.onProgress(progress)));
    this.subscriptions.add(this.bulkUploadService.summary$.subscribe(summary => this.onSummary(summary)));
    this.subscriptions.add(this.webSocketsService.receiveBulkUpload().subscribe(event => this.onBulkUploadEvent(event)));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get visible(): boolean {
    return !this.modalRef && (this.progress && this.progress.isProcessing || !!this.summary);
  }

  get processing(): boolean {
    return !this.summary;
  }

  open(): void {
    if (this.modalRef) { return; }
    if (this.summary) {
      this.showModal('summary');
    } else {
      this.showModal('progress');
    }
  }

  private onProgress(progress: UploadProgress): void {
    this.progress = progress;
    if (progress.isProcessing && !this.uploadActive) {
      this.uploadActive = true;
      this.showModal('progress');
    } else if (!progress.isProcessing && progress.status !== 'COMPLETED') {
      this.uploadActive = false;
      this.hideModal();
    }
    if (this.modalRef && !this.summary) {
      this.modalRef.content.progress = { ...progress };
    }
  }

  private onSummary(summary: BulkUploadSummary): void {
    this.summary = summary;
    this.uploadActive = false;
    // An open progress dialog turns into the summary; otherwise the snackbar waits to be clicked.
    if (this.modalRef) {
      this.modalRef.content.type = 'summary';
      this.modalRef.content.summary = summary;
      this.modalRef.content.loadJob();
    }
  }

  private onBulkUploadEvent(event: any): void {
    if (!event || (event.eventType !== 'BULK_UPLOAD_PROGRESS' && event.eventType !== 'BULK_UPLOAD_COMPLETED')) { return; }
    const payload = event.candidateDtoMap;
    if (!payload) { return; }
    this.zone.run(() => event.eventType === 'BULK_UPLOAD_PROGRESS'
      ? this.bulkUploadService.handleProgressEvent(payload, event.message)
      : this.bulkUploadService.handleCompletedEvent(payload, event.message));
  }

  private showModal(type: 'progress' | 'summary'): void {
    this.modalRef = this.bsModalService.show(BulkUploadResumeActionsComponent, {
      class: 'modal-lg',
      ignoreBackdropClick: true,
      keyboard: false,
      initialState: { type, progress: this.progress, summary: this.summary }
    });
    this.modalRef.content.onClose.subscribe((result: any) => {
      this.modalRef = null;
      if (result && result.action === 'close') {
        this.summary = null;
        this.bulkUploadService.closeSummary();
      }
    });
  }

  private hideModal(): void {
    if (this.modalRef) {
      this.modalRef.hide();
      this.modalRef = null;
    }
  }
}
